import React, { useState, useEffect, useMemo } from 'react';
import { X, Plus, Trash2, Save, ArrowRightLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';

// Parse mm:ss input string to total seconds (lenient)
const parseMMSS = (input) => {
  if (!input && input !== 0) return 0;
  const str = String(input).trim();
  if (str.includes(':')) {
    const [mm, ss] = str.split(':');
    const m = parseInt(mm, 10) || 0;
    const s = parseInt(ss, 10) || 0;
    return m * 60 + s;
  }
  return parseInt(str, 10) || 0;
};

const formatMMSS = (seconds) => {
  if (!seconds && seconds !== 0) return '00:00';
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

// Local utility: build round pairs from a flat steps array
const buildRoundsLocal = (steps = []) => {
  const rounds = [];
  for (let i = 0; i < steps.length; i += 2) {
    rounds.push({
      number: rounds.length + 1,
      a_idx: i,
      b_idx: i + 1 < steps.length ? i + 1 : null,
    });
  }
  const firstPhase = steps[0]?.phase || 'ventilation';
  const phaseA = firstPhase;
  const phaseB = firstPhase === 'apnea' ? 'ventilation' : 'apnea';
  return {
    rounds,
    headerA: phaseA === 'apnea' ? { label: 'Apnée', short: 'Bloquer', icon: '🔴' } : { label: 'Ventilation', short: 'Respirer', icon: '🟢' },
    headerB: phaseB === 'apnea' ? { label: 'Apnée', short: 'Bloquer', icon: '🔴' } : { label: 'Ventilation', short: 'Respirer', icon: '🟢' },
    phaseA,
    phaseB,
  };
};

const TableBuilderModal = ({ open, editingTable, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState('CO2');
  const [difficulty, setDifficulty] = useState('CUSTOM');
  const [description, setDescription] = useState('');
  const [tableDate, setTableDate] = useState(new Date().toISOString().slice(0, 10));
  const [steps, setSteps] = useState([
    { phase: 'ventilation', duration_input: '02:00' },
    { phase: 'apnea', duration_input: '01:00' },
  ]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (editingTable) {
      setName(editingTable.name || '');
      setType(editingTable.type || 'CO2');
      setDifficulty(editingTable.difficulty || 'CUSTOM');
      setDescription(editingTable.description || '');
      setTableDate(editingTable.table_date || new Date().toISOString().slice(0, 10));
      setSteps(
        (editingTable.steps || []).map((s) => ({
          phase: s.phase,
          duration_input: formatMMSS(s.duration_seconds),
        }))
      );
    } else {
      setName('');
      setType('CO2');
      setDifficulty('CUSTOM');
      setDescription('');
      setTableDate(new Date().toISOString().slice(0, 10));
      setSteps([
        { phase: 'ventilation', duration_input: '02:00' },
        { phase: 'apnea', duration_input: '01:00' },
      ]);
    }
    setError('');
  }, [editingTable, open]);

  const totalSeconds = steps.reduce((acc, s) => acc + parseMMSS(s.duration_input), 0);
  const apneaCount = steps.filter((s) => s.phase === 'apnea').length;
  const longestApnea = steps
    .filter((s) => s.phase === 'apnea')
    .reduce((max, s) => Math.max(max, parseMMSS(s.duration_input)), 0);

  const updateStep = (idx, field, value) => {
    const next = [...steps];
    next[idx] = { ...next[idx], [field]: value };
    setSteps(next);
  };

  // Switch starting phase: invert the phase of all steps (alternating sequence inverted)
  const handleSwitchStartPhase = () => {
    const inverted = steps.map((s) => ({
      ...s,
      phase: s.phase === 'apnea' ? 'ventilation' : 'apnea',
    }));
    setSteps(inverted);
  };

  // Add a new round (= 2 steps respecting the current alternation pattern)
  const addRound = () => {
    const firstPhase = steps[0]?.phase || 'ventilation';
    // For round N+1: phases continue alternation. Even-indexed step matches firstPhase, odd-indexed matches the opposite.
    const phaseA = firstPhase;
    const phaseB = firstPhase === 'apnea' ? 'ventilation' : 'apnea';
    const newA = { phase: phaseA, duration_input: phaseA === 'apnea' ? '01:00' : '02:00' };
    const newB = { phase: phaseB, duration_input: phaseB === 'apnea' ? '01:00' : '02:00' };
    setSteps([...steps, newA, newB]);
  };

  // Remove a specific round (both steps of that round)
  const removeRound = (a_idx, b_idx) => {
    const indicesToRemove = new Set([a_idx]);
    if (b_idx !== null && b_idx !== undefined) indicesToRemove.add(b_idx);
    setSteps(steps.filter((_, i) => !indicesToRemove.has(i)));
  };

  const handleSubmit = () => {
    setError('');
    if (!name.trim()) {
      setError('Le nom de la table est requis.');
      return;
    }
    if (steps.length === 0) {
      setError('Ajoutez au moins une étape.');
      return;
    }
    const parsedSteps = steps.map((s) => ({
      phase: s.phase,
      duration_seconds: parseMMSS(s.duration_input),
    }));
    if (parsedSteps.some((s) => s.duration_seconds <= 0)) {
      setError('Toutes les étapes doivent avoir une durée > 0.');
      return;
    }

    const payload = {
      name: name.trim(),
      type,
      difficulty,
      description: description.trim(),
      table_date: tableDate,
      steps: parsedSteps,
      is_template: false,
    };
    onSave(payload, editingTable?.id);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="table-builder-modal">
      <Card className="bg-blue-950/95 border-blue-700/50 max-w-3xl w-full max-h-[95vh] flex flex-col">
        <div className="p-5 border-b border-blue-800/50 flex items-center justify-between">
          <h3 className="text-2xl font-bold text-blue-100">
            {editingTable ? 'Modifier la table' : 'Nouvelle table d\'apnée'}
          </h3>
          <Button size="sm" variant="ghost" onClick={onClose} className="text-blue-300">
            <X size={20} />
          </Button>
        </div>

        <div className="overflow-y-auto p-5 space-y-4">
          {/* Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-blue-200 mb-1 block">Nom de la table *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Ma table CO2 perso"
                className="bg-blue-950/60 border-blue-700/50 text-blue-100"
                data-testid="table-name-input"
              />
            </div>
            <div>
              <Label className="text-blue-200 mb-1 block">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="bg-blue-950/60 border-blue-700/50 text-blue-100" data-testid="table-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CO2">CO2 (Hypercapnique)</SelectItem>
                  <SelectItem value="O2">O2 (Hypoxique)</SelectItem>
                  <SelectItem value="PV">PV (Poumons Vides)</SelectItem>
                  <SelectItem value="OTHER">Autres</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-blue-200 mb-1 block">Date</Label>
              <Input
                type="date"
                value={tableDate}
                onChange={(e) => setTableDate(e.target.value)}
                className="bg-blue-950/60 border-blue-700/50 text-blue-100"
                data-testid="table-date-input"
              />
            </div>
            <div>
              <Label className="text-blue-200 mb-1 block">Difficulté</Label>
              <Select value={difficulty} onValueChange={setDifficulty}>
                <SelectTrigger className="bg-blue-950/60 border-blue-700/50 text-blue-100" data-testid="table-difficulty-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FACILE">🟢 Facile</SelectItem>
                  <SelectItem value="MOYENNE">🟡 Moyenne</SelectItem>
                  <SelectItem value="DIFFICILE">🟠 Difficile</SelectItem>
                  <SelectItem value="TRES_DIFFICILE">🔴 Très difficile</SelectItem>
                  <SelectItem value="CUSTOM">⚪ Personnalisée</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-blue-200 mb-1 block">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Notes ou instructions pour cette table..."
              rows={2}
              className="bg-blue-950/60 border-blue-700/50 text-blue-100"
              data-testid="table-description-input"
            />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 p-3 bg-blue-900/40 rounded-lg border border-blue-800/40">
            <div className="text-center">
              <div className="text-xs text-blue-400 uppercase">Durée totale</div>
              <div className="text-xl font-bold text-cyan-300" data-testid="builder-total-duration">{formatMMSS(totalSeconds)}</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-blue-400 uppercase">Apnées</div>
              <div className="text-xl font-bold text-cyan-300">{apneaCount}</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-blue-400 uppercase">Max apnée</div>
              <div className="text-xl font-bold text-cyan-300">{formatMMSS(longestApnea)}</div>
            </div>
          </div>

          {/* Steps editor — grouped by round (2 columns) */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <Label className="text-blue-200">Rounds (cycle ventilation / apnée)</Label>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleSwitchStartPhase}
                  disabled={steps.length === 0}
                  variant="outline"
                  className="border-blue-600/50 text-blue-200 hover:bg-blue-800/50 h-8 text-xs"
                  data-testid="switch-start-phase-btn"
                  title="Inverser l'ordre des phases dans le round"
                >
                  <ArrowRightLeft size={14} className="mr-1" />
                  Inverser
                </Button>
                <Button
                  size="sm"
                  onClick={addRound}
                  className="bg-cyan-600 hover:bg-cyan-700 h-8 text-xs"
                  data-testid="add-round-btn"
                >
                  <Plus size={14} /> Ajouter un round
                </Button>
              </div>
            </div>

            {steps.length > 0 ? (
              (() => {
                const { rounds, headerA, headerB } = buildRoundsLocal(steps);
                return (
                  <div className="border border-blue-700/40 rounded-lg overflow-hidden" data-testid="rounds-editor">
                    {/* Header row */}
                    <div className="grid grid-cols-[60px_1fr_1fr_60px] bg-blue-900/50 text-blue-200 text-xs uppercase tracking-wider">
                      <div className="text-center py-2 px-2 border-r border-blue-700/40">Round</div>
                      <div className="text-center py-2 px-2 border-r border-blue-700/40">
                        <div>{headerA.icon} {headerA.label}</div>
                        <div className="text-[10px] opacity-70 normal-case">({headerA.short})</div>
                      </div>
                      <div className="text-center py-2 px-2 border-r border-blue-700/40">
                        <div>{headerB.icon} {headerB.label}</div>
                        <div className="text-[10px] opacity-70 normal-case">({headerB.short})</div>
                      </div>
                      <div className="text-center py-2 px-2"></div>
                    </div>
                    {/* Round rows */}
                    <div className="max-h-[40vh] overflow-y-auto">
                      {rounds.map((r) => {
                        const stepA = steps[r.a_idx];
                        const stepB = r.b_idx !== null ? steps[r.b_idx] : null;
                        return (
                          <div
                            key={r.number}
                            className="grid grid-cols-[60px_1fr_1fr_60px] border-t border-blue-800/40 hover:bg-blue-900/20"
                            data-testid={`round-row-${r.number}`}
                          >
                            <div className="flex items-center justify-center py-2 px-2 text-blue-300 font-mono font-bold border-r border-blue-800/40">
                              {r.number}
                            </div>
                            <div className={`flex items-center justify-center py-2 px-2 border-r border-blue-800/40 ${headerA.icon === '🔴' ? 'bg-red-900/15' : 'bg-green-900/15'}`}>
                              <Input
                                value={stepA?.duration_input || ''}
                                onChange={(e) => updateStep(r.a_idx, 'duration_input', e.target.value)}
                                onBlur={(e) => updateStep(r.a_idx, 'duration_input', formatMMSS(parseMMSS(e.target.value)))}
                                placeholder="mm:ss"
                                className="bg-blue-950/60 border-blue-700/50 text-blue-100 font-mono text-center w-24 h-9"
                                data-testid={`round-${r.number}-a-input`}
                              />
                            </div>
                            <div className={`flex items-center justify-center py-2 px-2 border-r border-blue-800/40 ${headerB.icon === '🔴' ? 'bg-red-900/15' : 'bg-green-900/15'}`}>
                              {stepB ? (
                                <Input
                                  value={stepB.duration_input || ''}
                                  onChange={(e) => updateStep(r.b_idx, 'duration_input', e.target.value)}
                                  onBlur={(e) => updateStep(r.b_idx, 'duration_input', formatMMSS(parseMMSS(e.target.value)))}
                                  placeholder="mm:ss"
                                  className="bg-blue-950/60 border-blue-700/50 text-blue-100 font-mono text-center w-24 h-9"
                                  data-testid={`round-${r.number}-b-input`}
                                />
                              ) : (
                                <span className="text-blue-500 text-xs italic">— (incomplet)</span>
                              )}
                            </div>
                            <div className="flex items-center justify-center py-2 px-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeRound(r.a_idx, r.b_idx)}
                                className="h-8 w-8 p-0 text-red-300 hover:bg-red-900/30"
                                data-testid={`remove-round-${r.number}`}
                                title="Supprimer ce round"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="border border-dashed border-blue-700/50 rounded-lg text-center py-8 text-blue-400 text-sm" data-testid="empty-rounds">
                Aucun round. Cliquez sur "Ajouter un round" pour commencer.
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-900/30 border border-red-700/50 text-red-200 text-sm p-3 rounded-lg" data-testid="builder-error">
              {error}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-blue-800/50 flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose} className="border-blue-600/50 text-blue-200">
            Annuler
          </Button>
          <Button onClick={handleSubmit} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-table-btn">
            <Save className="mr-2" size={16} />
            Enregistrer
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default TableBuilderModal;
